import { useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { KeyRound, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function MasterLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user && user.role === "admin") return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) navigate("/admin");
    else setError(res.error);
  };

  const inputCls =
    "bg-[#0D1B2A] border border-[#F8F6F0]/15 focus:border-[#C5A059] focus:ring-2 focus:ring-[#C5A059]/25 rounded-xl px-4 py-3.5 text-[#F8F6F0] placeholder:text-[#F8F6F0]/35 outline-none w-full transition-all text-sm";

  return (
    <div className="min-h-screen bg-[#0A192F] flex items-center justify-center px-4 relative overflow-hidden" data-testid="master-login-page">
      <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-[#C5A059]/8 blur-3xl pointer-events-none" />
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md"
      >
        <Link to="/" className="inline-flex items-center gap-2 text-[#F8F6F0]/50 hover:text-[#C5A059] text-xs uppercase tracking-widest transition-colors mb-8" data-testid="back-to-site-link">
          <ArrowLeft size={13} /> Back to site
        </Link>
        <div className="bg-[#0D1B2A] border border-[#C5A059]/20 rounded-3xl p-8 sm:p-10 shadow-2xl">
          <div className="w-14 h-14 rounded-full bg-[#C5A059]/15 border border-[#C5A059]/30 flex items-center justify-center text-[#C5A059] mb-6">
            <KeyRound size={24} />
          </div>
          <h1 className="font-serif-display text-3xl font-bold text-[#F8F6F0]" data-testid="master-login-title">Master Login</h1>
          <p className="text-[#F8F6F0]/50 text-sm mt-2">Hotel management access only.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="master-login-form">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[#F8F6F0]/50 block mb-1.5">Email</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@eufaulahotel.com" className={inputCls} data-testid="login-email-input" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[#F8F6F0]/50 block mb-1.5">Password</label>
              <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className={inputCls} data-testid="login-password-input" />
            </div>
            {error && (
              <p className="text-red-400 text-sm bg-red-400/10 border border-red-400/25 rounded-xl px-4 py-2.5" data-testid="login-error-message">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#C5A059] text-[#0A192F] hover:bg-[#D4AF37] rounded-full py-4 font-semibold transition-all duration-300 disabled:opacity-60"
              data-testid="login-submit-button"
            >
              {loading ? "Signing in…" : "Sign In"}
            </button>
          </form>
        </div>
      </motion.div>
    </div>
  );
}
